import { Card, SLabel, ICell } from '../components/Atoms';
import { C } from '../constants';
import { toDay, toTime, fmtDay } from '../utils';
import { useEffect, useRef, useState } from 'react';

// ── Clinical Notes Parser ─────────────────────────────────────────────────────
function parseNotes(raw) {
  const result = {};

  // Normalise line endings
  const text = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Labels mapped to our fields — ordered longest-first to avoid prefix clashes
  const SECTIONS = [
    // CC — Chief Complaint
    { field: 'cc',  patterns: ['chief complaint', 'complaint', 'cc'] },
    // HPI — History of Present Illness
    { field: 'hpi', patterns: ['history of present illness', 'history of presenting illness', 'history', 'hpi'] },
    // PMH — Past Medical History
    { field: 'pmh', patterns: ['past medical history', 'past history', 'pmh'] },
    // Meds — appended into PMH field
    { field: 'pmh_meds', patterns: ['current medications', 'home medications', 'medications', 'current meds', 'meds'] },
    // Allergies — appended into PMH field
    { field: 'pmh_allergies', patterns: ['allergies', 'allergy', 'nkda'] },
    // FH / PSH / ROS
    { field: 'fh',  patterns: ['family medical history', 'family history', 'fmh', 'fh'] },
    { field: 'psh', patterns: ['personal and social history', 'personal & social history', 'social history', 'personal history', 'social hx', 'psh'] },
    { field: 'ros', patterns: ['review of systems', 'ros'] },
    // Sexual History
    { field: 'sexualHx', patterns: ['sexual history', 'sexual hx', 'sexhx', 'sex hx'] },
    // PE — Physical Exam
    { field: 'pe',  patterns: ['physical examination', 'physical exam', 'objective', 'vitals', 'vs', 'pe', 'o>'] },
    // Diagnosis / Plan
    { field: 'diagnosis', patterns: ['impression', 'assessment', 'final diagnosis', 'working diagnosis', 'diagnosis', 'dx'] },
    { field: 'plan', patterns: ['assessment and plan', 'management', 'plan'] },
  ];

  // Build a regex that matches any section header
  // Matches: "LABEL:" or "LABEL>" or "LABEL\n" at start of line (case-insensitive)

  // Split text into labelled chunks
  // Strategy: find each header, grab everything until the next header
  const lines = text.split('\n');
  const chunks = []; // { label, startLine, lines[] }
  let current = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Check if this line is a section header
    let matchedField = null;
    for (const sec of SECTIONS) {
      for (const pat of sec.patterns) {
        const re = new RegExp('^' + pat.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*[:\\>]?\\s*$', 'i');
        // Also handle "LABEL: content on same line"
        const inlineRe = new RegExp('^' + pat.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*[:\\>]\\s*(.+)', 'i');
        if (re.test(trimmed)) {
          matchedField = sec.field;
          if (current) chunks.push(current);
          current = { field: sec.field, lines: [] };
          break;
        }
        const inlineMatch = trimmed.match(inlineRe);
        if (inlineMatch) {
          matchedField = sec.field;
          if (current) chunks.push(current);
          current = { field: sec.field, lines: [inlineMatch[1].trim()] };
          break;
        }
      }
      if (matchedField) break;
    }

    if (!matchedField && current) {
      current.lines.push(line);
    }
  }
  if (current) chunks.push(current);

  // Collapse each chunk's lines into text
  for (const chunk of chunks) {
    const val = chunk.lines.join('\n').trim();
    if (!val) continue;
    if (chunk.field === 'pmh_meds') {
      // Append Meds into PMH field
      result.pmh = result.pmh ? result.pmh + '\n\nMeds: ' + val : 'Meds: ' + val;
    } else if (chunk.field === 'pmh_allergies') {
      // Append Allergies into PMH field
      result.pmh = result.pmh ? result.pmh + '\n\nAllergies: ' + val : 'Allergies: ' + val;
    } else if (chunk.field === 'plan') {
      result._plan = val;
    } else {
      result[chunk.field] = val;
    }
  }

  // ── Vitals extraction from O> / PE block ──────────────────────────────────
  // Looks for the pattern: BP\nHR\nRR\nTemp\nO2 as sequential bare numbers
  const peText = result.pe || '';
  const vitalLines = peText.split('\n').map(l => l.trim()).filter(Boolean);

  // Pattern: lines that look like "110/70", "109", "19", "36.5", "98%"
  const bpRe   = /^\d{2,3}\/\d{2,3}$/;
  const numRe  = /^\d{2,3}(\.\d)?$/;
  const o2Re   = /^\d{2,3}%?$/;
  const tempRe = /^3[5-9](\.\d)?$|^4[0-2](\.\d)?$/;

  const vitals = {};
  let vIdx = 0;
  for (let i = 0; i < vitalLines.length && vIdx < 5; i++) {
    const l = vitalLines[i];
    if (vIdx === 0 && bpRe.test(l))  { vitals.bp = l; vIdx++; continue; }
    if (vIdx === 1 && numRe.test(l)) { vitals.hr = l; vIdx++; continue; }
    if (vIdx === 2 && numRe.test(l)) { vitals.rr = l; vIdx++; continue; }
    if (vIdx === 3 && (numRe.test(l) || tempRe.test(l))) { vitals.temp = l; vIdx++; continue; }
    if (vIdx === 4 && o2Re.test(l))  { vitals.o2 = l.replace('%',''); vIdx++; continue; }
  }
  if (Object.keys(vitals).length > 0) result._vitals = vitals;

  // ── Diagnosis: also check CC as fallback label ─────────────────────────────
  // If there's no diagnosis but there is an impression in the raw text
  if (!result.diagnosis) {
    const impRe = /^(?:impression|dx|assessment)[:\s]+(.+)/im;
    const m = raw.match(impRe);
    if (m) result.diagnosis = m[1].trim();
  }

  return result;
}

// ── Parse Modal ───────────────────────────────────────────────────────────────
function ParseModal({ onApply, onClose }) {
  const [raw, setRaw]       = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError]   = useState('');

  // Grouped preview layout: each group has a header colour + which fields belong
  const PREVIEW_GROUPS = [
    { label: 'CC — Chief Complaint',              color: '#8E8E9A', fields: ['cc'] },
    { label: 'HPI — History of Present Illness',  color: '#8E8E9A', fields: ['hpi'] },
    { label: 'PMH — Past Medical History',        color: '#8E8E9A', fields: ['pmh'] },
    { label: 'FH — Family History',               color: '#8E8E9A', fields: ['fh'] },
    { label: 'PSH — Personal & Social History',   color: '#8E8E9A', fields: ['psh'] },
    { label: 'Sexual History',                      color: '#8E8E9A', fields: ['sexualHx'] },
    { label: 'PE — Physical Exam',                color: '#8E8E9A', fields: ['pe', '_vitals'] },
    { label: 'Other',                             color: '#8E8E9A', fields: ['ros', 'diagnosis', '_plan'] },
  ];
  const FIELD_SUBLABELS = {
    cc: 'Chief Complaint', hpi: 'HPI', pmh: 'Past Medical History',
    medications: 'Medications', allergies: 'Allergies',
    pe: 'Physical Exam', _vitals: 'Vitals detected',
    fh: 'Family History', psh: 'Personal & Social History', sexualHx: 'Sexual History',
    ros: 'Review of Systems', diagnosis: 'Impression / Diagnosis', _plan: 'Plan',
  };

  const handleParse = () => {
    if (!raw.trim()) { setError('Paste your clinical notes first.'); return; }
    const parsed = parseNotes(raw);
    const fields = Object.keys(parsed).filter(k => parsed[k] && (typeof parsed[k] === 'string' ? parsed[k].trim() : Object.keys(parsed[k]).length > 0));
    if (fields.length === 0) { setError("Couldn't detect any sections. Make sure your note has labels like CC:, HPI:, PMH:."); return; }
    setError('');
    setPreview(parsed);
  };

  const overlay = { position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
  const sheet   = { background: C.bg1, borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={sheet}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.bd}`, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.tx }}>📋 Parse Clinical Notes</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>Recognises: CC / Complaint · HPI / History · PMH / Meds / Allergies · PE / Vitals / VS</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer', padding: '0 4px' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
          {!preview ? (
            <>
              <textarea
                value={raw}
                onChange={e => setRaw(e.target.value)}
                placeholder={`Paste your resident's note here…\n\nCC: Non-healing wound, left foot\n\nHPI:\nPatient was apparently well until…\n\nPMH:\nPulmonary Tuberculosis (October 2023)\nMeds: Clindamycin 300mg Q8\nAllergies: NKDA\n\nVS:\n110/70\n109\n19\n36.5\n98%`}
                rows={14}
                style={{ width: '100%', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '10px 12px', color: C.tx, fontSize: 12, lineHeight: 1.6, resize: 'vertical', fontFamily: 'monospace', boxSizing: 'border-box' }}
              />
              {error && <div style={{ color: '#FF6B6B', fontSize: 12, marginTop: 8 }}>{error}</div>}
              <button onClick={handleParse}
                style={{ width: '100%', marginTop: 12, padding: '12px 0', borderRadius: 10, background: '#1A3D6A', border: 'none', color: '#7DC4FF', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                Parse Notes →
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, color: C.dim, marginBottom: 14 }}>
                Review what was detected. Tap <strong style={{ color: '#30D158' }}>Apply to Chart</strong> to fill in the fields.
              </div>
              {PREVIEW_GROUPS.map(grp => {
                const hits = grp.fields.filter(f => {
                  const v = preview[f];
                  return v && (typeof v === 'string' ? v.trim() : Object.keys(v).length > 0);
                });
                if (hits.length === 0) return null;
                return (
                  <div key={grp.label} style={{ marginBottom: 14, background: C.bg3, border: `1px solid ${grp.color}33`, borderLeft: `3px solid ${grp.color}`, borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: grp.color, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '8px 12px 6px', borderBottom: `1px solid ${grp.color}22` }}>{grp.label}</div>
                    {hits.map(f => {
                      const val = preview[f];
                      const display = f === '_vitals'
                        ? Object.entries(val).map(([k,v]) => `${k.toUpperCase()}: ${v}`).join('  ·  ')
                        : val;
                      return (
                        <div key={f} style={{ padding: '8px 12px', borderBottom: `1px solid ${C.bd}` }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{FIELD_SUBLABELS[f] || f}</div>
                          <div style={{ fontSize: 12, color: C.tx, lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: f === '_vitals' ? 'inherit' : 'monospace' }}>{display}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button onClick={() => setPreview(null)}
                  style={{ flex: 1, padding: '10px 0', borderRadius: 10, background: 'none', border: `1px solid ${C.bd}`, color: C.dim, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  ← Re-paste
                </button>
                <button onClick={() => onApply(preview)}
                  style={{ flex: 2, padding: '10px 0', borderRadius: 10, background: '#0A2A14', border: '1px solid #30D158', color: '#30D158', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  ✓ Apply to Chart
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const AutoTA = ({ value, onChange, placeholder }) => {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };
  useEffect(resize, [value]);
  return (
    <textarea ref={ref} value={value || ''} onChange={e => { onChange(e.target.value); resize(); }} placeholder={placeholder} rows={1}
      style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '5px 8px', color: C.dim, fontSize: 12, lineHeight: 1.5, resize: 'none', overflow: 'hidden', display: 'block', minHeight: '2rem' }} />
  );
};

const TA = ({ value, onChange, placeholder }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [value]);
  return (
    <textarea ref={ref} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={1}
      style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '6px 8px', color: C.tx, fontSize: 12, lineHeight: 1.5, resize: 'none', overflow: 'hidden', display: 'block' }} />
  );
};


// ── OB-GYN History (shown only for female patients) ──────────────────────────
function calcAOG(lmpStr) {
  if (!lmpStr) return null;
  // Use Philippine Time (UTC+8) for both dates
  const PH_OFFSET = 8 * 60 * 60 * 1000;
  const [y, m, d] = lmpStr.split('-').map(Number);
  const lmpMidnight = Date.UTC(y, m - 1, d) - PH_OFFSET; // PH midnight of LMP
  const nowPH = Date.now() + PH_OFFSET;
  const todayMidnightPH = Math.floor(nowPH / (1000 * 60 * 60 * 24)) * (1000 * 60 * 60 * 24);
  const lmpMidnightPH  = Math.floor(lmpMidnight / (1000 * 60 * 60 * 24)) * (1000 * 60 * 60 * 24);
  const days = Math.floor((todayMidnightPH - lmpMidnightPH) / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const rem   = days % 7;
  return { weeks, days: rem, totalDays: days };
}
function calcEDD(lmpStr) {
  if (!lmpStr) return null;
  const lmp = new Date(lmpStr);
  const month = lmp.getMonth() + 1; // 1-12
  let d = lmp.getDate() + 7;
  let m, y;
  if (month <= 3) {
    // Jan–Mar: +9 months, +7 days
    m = month + 9;
    y = lmp.getFullYear();
  } else {
    // Apr–Dec: -3 months, +7 days, +1 year
    m = month - 3;
    y = lmp.getFullYear() + 1;
  }
  // Handle day overflow
  const edd = new Date(y, m - 1, d);
  return edd.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}
function calcGPTPAL(pregnancies) {
  if (!pregnancies || pregnancies.length === 0) return '';
  const G = pregnancies.length;
  let T=0, Pr=0, A=0, L=0;
  pregnancies.forEach(pg => {
    const term = (pg.term || '').toLowerCase();
    if (term === 'abortion' || term === 'miscarriage' || term === 'ectopic') {
      // A: ended at or before 20 weeks
      A++;
    } else if (term === 'preterm') {
      // Preterm: >20 and <37 weeks
      Pr++;
      if (pg.living === true) L++;
    } else {
      // Term: >=37 weeks
      T++;
      if (pg.living === true) L++;
    }
  });
  const P = T + Pr; // P: deliveries >=20 weeks
  return `G${G}P${P} (${T}-${Pr}-${A}-${L})`;
}

function OBGYNSection({ cur, patch }) {
  const ob   = cur.obgyn || {};
  const preg = ob.pregnancies || [];

  const setOb = (field, val) =>
    patch(p => ({ ...p, obgyn: { ...(p.obgyn || {}), [field]: val } }));

  const setGyn = (field, val) =>
    patch(p => ({ ...p, obgyn: { ...(p.obgyn || {}), gyn: { ...(p.obgyn?.gyn || {}), [field]: val } } }));

  const addPreg = () =>
    patch(p => ({ ...p, obgyn: { ...(p.obgyn || {}),
      pregnancies: [...(p.obgyn?.pregnancies || []), { id: `g${Date.now()}`, year: '', gender: '', term: 'Term', delivery: 'NSD', hospital: '', complications: '' }]
    }}));

  const editPreg = (id, field, val) =>
    patch(p => ({ ...p, obgyn: { ...(p.obgyn || {}),
      pregnancies: (p.obgyn?.pregnancies || []).map(g => g.id === id ? { ...g, [field]: val } : g)
    }}));

  const delPreg = id =>
    patch(p => ({ ...p, obgyn: { ...(p.obgyn || {}),
      pregnancies: (p.obgyn?.pregnancies || []).filter(g => g.id !== id)
    }}));

  const aog  = calcAOG(ob.lmp);
  const edd  = calcEDD(ob.lmp);
  const gpScore = calcGPTPAL(preg);
  const gyn  = ob.gyn || {};

  const lbl = (txt) => (
    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>{txt}</div>
  );
  const inputStyle = { width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '6px 8px', color: C.tx, fontSize: 12, outline: 'none' };
  const smallInput = { ...inputStyle, fontSize: 11 };

  return (
    <div style={{ marginBottom: 14, borderTop: `1px solid ${C.bd}`, paddingTop: 12, paddingLeft: 12, borderLeft: `2px solid ${C.bd}`, marginLeft: 4 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
        OB-GYN History
      </div>

      {/* LMP */}
      <div style={{ marginBottom: 10 }}>
        {lbl('LMP — Last Menstrual Period')}
        <input type="date" value={ob.lmp || ''} onChange={e => setOb('lmp', e.target.value)}
          style={{ ...inputStyle, colorScheme: 'dark' }} />

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, fontSize: 11, color: C.dim, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!ob.pregnant} onChange={e => setOb('pregnant', e.target.checked)}
            style={{ accentColor: '#4A9EFF' }} />
          Currently pregnant
          {ob.pregnant && aog && <span style={{ color: '#FF9500', fontWeight: 700 }}> · AOG {aog.weeks}w{aog.days}d · EDD {edd}</span>}
        </label>
      </div>

      {/* Obstetric History */}
      <div style={{ marginBottom: 10 }}>
        {lbl('Obstetric History')}
        {gpScore ? (
          <div style={{ fontSize: 12, fontWeight: 700, color: C.tx, marginBottom: 6 }}>{gpScore}</div>
        ) : (
          <div style={{ fontSize: 11, color: C.dim2, marginBottom: 6, fontStyle: 'italic' }}>No pregnancies recorded</div>
        )}
        {preg.map((g, idx) => (
          <div key={g.id} style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>G{idx + 1}</span>
              <button onClick={() => delPreg(g.id)}
                style={{ background: 'none', border: 'none', color: '#FF454566', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: '0 2px' }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px' }}>
              <div>
                <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Year</div>
                <input value={g.year} onChange={e => editPreg(g.id, 'year', e.target.value)} placeholder="e.g. 2019"
                  style={smallInput} />
              </div>
              <div>
                <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Gender of Child</div>
                <select value={g.gender} onChange={e => editPreg(g.id, 'gender', e.target.value)}
                  style={{ ...smallInput }}>
                  <option value="">—</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Term</div>
                <select value={g.term} onChange={e => editPreg(g.id, 'term', e.target.value)}
                  style={{ ...smallInput }}>
                  <option value="Term">Term (≥37 wks)</option>
                  <option value="Preterm">Preterm (&gt;20, &lt;37 wks)</option>
                  <option value="Abortion">Abortion (≤20 wks)</option>
                  <option value="Miscarriage">Miscarriage (≤20 wks)</option>
                  <option value="Ectopic">Ectopic</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Delivery</div>
                <select value={g.delivery} onChange={e => editPreg(g.id, 'delivery', e.target.value)}
                  style={{ ...smallInput }}>
                  <option value="NSD">NSD</option>
                  <option value="CS">CS</option>
                  <option value="Forceps">Forceps</option>
                  <option value="Vacuum">Vacuum</option>
                  <option value="N/A">N/A</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Hospital</div>
                <input value={g.hospital} onChange={e => editPreg(g.id, 'hospital', e.target.value)} placeholder="Hospital name"
                  style={smallInput} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Complications</div>
                <input value={g.complications} onChange={e => editPreg(g.id, 'complications', e.target.value)} placeholder="(-) complications"
                  style={smallInput} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 4 }}>Living Status</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[{ val: true, label: 'Living' }, { val: false, label: 'Not Living' }].map(opt => (
                    <button key={String(opt.val)} onClick={() => editPreg(g.id, 'living', opt.val)}
                      style={{ flex: 1, padding: '4px 0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        border: `1px solid ${g.living === opt.val ? '#4A9EFF' : C.bd}`,
                        background: g.living === opt.val ? '#1A3D6A' : 'transparent',
                        color: g.living === opt.val ? '#7DC4FF' : C.dim }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
        <button onClick={addPreg}
          style={{ fontSize: 11, color: C.dim, background: 'none', border: `1px dashed ${C.bd}`, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', width: '100%' }}>
          + Add Pregnancy (G{preg.length + 1})
        </button>
      </div>

      {/* Gynecologic History */}
      <div style={{ marginBottom: 10 }}>
        {lbl('Gynecologic History — Menstruation')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Menarche (age)</div>
            <input value={gyn.menarche || ''} onChange={e => setGyn('menarche', e.target.value)} placeholder="e.g. 13"
              style={smallInput} />
          </div>
          <div>
            <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Interval (days)</div>
            <input value={gyn.interval || ''} onChange={e => setGyn('interval', e.target.value)} placeholder="e.g. 28"
              style={smallInput} />
          </div>
          <div>
            <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Duration (days)</div>
            <input value={gyn.duration || ''} onChange={e => setGyn('duration', e.target.value)} placeholder="e.g. 5"
              style={smallInput} />
          </div>
          <div>
            <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Amount (ppd)</div>
            <input value={gyn.amount || ''} onChange={e => setGyn('amount', e.target.value)} placeholder="e.g. 3 ppd"
              style={smallInput} />
          </div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 8, color: C.dim, textTransform: 'uppercase', marginBottom: 2 }}>Symptoms</div>
          <input value={gyn.symptoms || ''} onChange={e => setGyn('symptoms', e.target.value)} placeholder="Dysmenorrhea, spotting, etc."
            style={smallInput} />
        </div>

        {lbl('Gynecologic History — Surgeries')}
        <input value={gyn.surgeries || ''} onChange={e => setGyn('surgeries', e.target.value)} placeholder="e.g. (-) previous gynecologic surgeries"
          style={{ ...smallInput, marginBottom: 8 }} />

        {lbl('Pap Smear')}
        <input type="date" value={gyn.papSmear || ''} onChange={e => setGyn('papSmear', e.target.value)}
          style={{ ...smallInput, colorScheme: 'dark' }} />
        {gyn.papSmear && (
          <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>
            Last pap smear: {new Date(gyn.papSmear).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        )}
      </div>
    </div>
  );
}

const Section = ({ label, field, cur, setFld }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>{label}</div>
    <TA value={cur[field]} onChange={v => setFld(field, v)} placeholder={`${label}…`} />
  </div>
);

const STATUS_OPTS = ['Completed', 'Pending', 'Scheduled', 'Cancelled'];
const STATUS_COL  = { Completed: '#30D158', Pending: '#FF9500', Scheduled: '#4A9EFF', Cancelled: '#FF4545' };

export default function IssuesTab({ cur, setFld, patch, showResolved, setShowResolved, onNotify }) {
  const issuesList      = cur.issuesList     || [];
  const resolvedIssues  = cur.resolvedIssues || [];
  const procedureList   = cur.procedureList  || [];
  const [showParse, setShowParse]   = useState(false);
  const [parseFlash, setParseFlash] = useState(false);

  const handleApplyParsed = (parsed) => {
    const MAP = { cc: 'cc', hpi: 'hpi', pmh: 'pmh', fh: 'fh', psh: 'psh', ros: 'ros', pe: 'pe', diagnosis: 'diagnosis', sexualHx: 'sexualHx' };
    // Apply all fields in a single patch to avoid race condition where
    // multiple setFld calls each read stale pts and overwrite each other
    const updates = {};
    Object.entries(MAP).forEach(([src, fld]) => {
      if (parsed[src] && parsed[src].trim()) updates[fld] = parsed[src].trim();
    });
    if (Object.keys(updates).length > 0) {
      patch(p => ({ ...p, ...updates }));
    }
    setShowParse(false);
    setParseFlash(true);
    setTimeout(() => setParseFlash(false), 2500);
  };

  // ── Active issues ──
  const addIssue     = () => patch(p => ({ ...p, issuesList: [...(p.issuesList || []), { id: `i${Date.now()}`, text: '', detail: '', date: toDay() }] }));
  const editIssue    = (id, f, v) => patch(p => ({ ...p, issuesList: (p.issuesList || []).map(i => i.id === id ? { ...i, [f]: v } : i) }));
  const resolveIssue = id => {
    const issue = issuesList.find(i => i.id === id);
    if (!issue) return;
    patch(p => ({
      ...p,
      issuesList:     (p.issuesList     || []).filter(i => i.id !== id),
      resolvedIssues: [...(p.resolvedIssues || []), { ...issue, resolvedDate: toDay(), resolvedTime: toTime() }],
    }));
  };
  const unresolveIssue = id => {
    const issue = resolvedIssues.find(i => i.id === id);
    if (!issue) return;
    patch(p => ({
      ...p,
      resolvedIssues: (p.resolvedIssues || []).filter(i => i.id !== id),
      issuesList:     [...(p.issuesList  || []), { ...issue, resolvedDate: undefined, resolvedTime: undefined }],
    }));
  };
  const delIssue    = id => patch(p => ({ ...p, issuesList:     (p.issuesList     || []).filter(i => i.id !== id) }));
  const delResolved = id => patch(p => ({ ...p, resolvedIssues: (p.resolvedIssues || []).filter(i => i.id !== id) }));

  // ── Procedures ──
  const addProc  = () => {
    patch(p => ({ ...p, procedureList: [...(p.procedureList || []), { id: `pr${Date.now()}`, name: '', date: toDay(), physician: '', status: 'Pending', note: '' }] }));
    // notification fires on name blur (see procedure row below)
  };
  const editProc = (id, f, v) => patch(p => ({ ...p, procedureList: (p.procedureList || []).map(pr => pr.id === id ? { ...pr, [f]: v } : pr) }));
  const delProc  = id => patch(p => ({ ...p, procedureList: (p.procedureList || []).filter(pr => pr.id !== id) }));

  const [issuesView, setIssuesView] = useState('issues');
  const subTab = (k, label) => (
    <button onClick={() => setIssuesView(k)} style={{
      flex: 1, padding: '8px 4px', borderRadius: 8, cursor: 'pointer',
      fontWeight: 700, fontSize: 12,
      border: `1px solid ${issuesView === k ? '#7DC4FF' : C.bd}`,
      background: issuesView === k ? '#1A3D6A' : 'transparent',
      color: issuesView === k ? '#7DC4FF' : C.dim,
      transition: 'all 0.15s',
    }}>{label}</button>
  );

  return (
    <>
      {/* Subtab bar — 3 cols */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 16 }}>
        {subTab('issues',  'Issues')}
        {subTab('history', 'History')}
        {subTab('surgery', 'Surgery')}
      </div>

      {/* ── ISSUES TAB ── */}
      {issuesView === 'issues' && <>
      {/* Active Issues */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <SLabel mb={0}>Active Issues</SLabel>
          <span style={{ marginLeft: 6, fontSize: 10, color: '#FF9500', fontWeight: 700, background: '#FF950022', padding: '1px 6px', borderRadius: 4 }}>{issuesList.length}</span>
          <button onClick={addIssue} style={{ marginLeft: 'auto', background: '#1A3D6A', color: '#7DC4FF', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>
            + Add Issue
          </button>
        </div>
        {issuesList.length === 0 && (
          <div style={{ color: C.dim2, fontSize: 13, padding: '8px 0', textAlign: 'center' }}>
            No active issues
            {cur.activeIssues && (
              <div style={{ marginTop: 10, padding: 10, background: C.bg3, borderRadius: 8, textAlign: 'left', color: C.dim, fontSize: 12, lineHeight: 1.6, borderLeft: `2px solid ${C.bd}` }}>
                {cur.activeIssues}
              </div>
            )}
          </div>
        )}
        {issuesList.map((issue, idx) => (
          <div key={issue.id} style={{ borderBottom: idx < issuesList.length - 1 ? `1px solid ${C.bg0}` : 'none', paddingBottom: 10, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF9500', flexShrink: 0, marginTop: 5 }} />
              <div style={{ flex: 1 }}>
                <input value={issue.text} onChange={e => editIssue(issue.id, 'text', e.target.value)} placeholder="Issue title…"
                  style={{ width: '100%', background: 'transparent', border: 'none', color: '#E0EEFA', fontSize: 13, fontWeight: 600, outline: 'none', marginBottom: 4 }} />
                <AutoTA value={issue.detail} onChange={v => editIssue(issue.id, 'detail', v)} placeholder="Details, plan, context…" />
                <div style={{ fontSize: 10, color: C.dim2, marginTop: 3 }}>Added {fmtDay(issue.date)}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                <button onClick={() => resolveIssue(issue.id)}
                  style={{ background: '#0A2010', border: '1px solid #30D15844', borderRadius: 6, color: '#30D158', cursor: 'pointer', fontSize: 11, padding: '4px 8px', fontWeight: 700 }}>
                  ✓ Resolve
                </button>
                <button onClick={() => delIssue(issue.id)}
                  style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 14, padding: '2px 4px' }}>×</button>
              </div>
            </div>
          </div>
        ))}
      </Card>

      {/* Resolved Issues */}
      <div style={{ marginBottom: 12 }}>
        <button onClick={() => setShowResolved(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: C.bg2, border: `1px solid ${C.bd}`, borderRadius: showResolved ? '10px 10px 0 0' : 10, padding: '10px 14px', cursor: 'pointer', color: C.dim }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#30D158', letterSpacing: '0.08em', textTransform: 'uppercase' }}>✓ Resolved Issues</span>
          <span style={{ fontSize: 10, color: '#30D158', background: '#30D15822', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>{resolvedIssues.length}</span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: C.dim }}>{showResolved ? '▲' : '▼'}</span>
        </button>
        {showResolved && (
          <div style={{ background: C.bg2, borderRadius: '0 0 10px 10px', border: `1px solid ${C.bd}`, borderTop: 'none', padding: '4px 14px 12px' }}>
            {resolvedIssues.length === 0 && <div style={{ color: C.dim2, fontSize: 13, padding: '12px 0', textAlign: 'center' }}>No resolved issues yet</div>}
            {[...resolvedIssues].reverse().map((issue, idx) => (
              <div key={issue.id} style={{ borderBottom: idx < resolvedIssues.length - 1 ? `1px solid ${C.bg0}` : 'none', paddingBottom: 8, marginBottom: 8, paddingTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#30D158', flexShrink: 0, marginTop: 4 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#5A8A6A', textDecoration: 'line-through', marginBottom: 2 }}>{issue.text || 'Untitled issue'}</div>
                    {issue.detail && <div style={{ fontSize: 12, color: C.dim2, lineHeight: 1.4, marginBottom: 3 }}>{issue.detail}</div>}
                    <div style={{ fontSize: 10, color: C.dim2 }}>
                      Resolved {issue.resolvedDate ? fmtDay(issue.resolvedDate) : '—'}{issue.resolvedTime ? ` · ${issue.resolvedTime}` : ''} · Added {fmtDay(issue.date)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                    <button onClick={() => unresolveIssue(issue.id)}
                      style={{ background: '#1A1A00', border: '1px solid #FF950044', borderRadius: 6, color: '#FF9500', cursor: 'pointer', fontSize: 10, padding: '3px 7px', fontWeight: 700 }}>
                      ↩ Reopen
                    </button>
                    <button onClick={() => delResolved(issue.id)}
                      style={{ background: 'none', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}>×</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Procedures */}

      {/* Endorsements */}
      <Card s={{ background: '#0A1E0E', border: '1px solid #1A3A1E' }}>
        <SLabel color="#30D158">Endorsements</SLabel>
        <textarea value={cur.note || ''} onChange={e => setFld('note', e.target.value)} placeholder="Add endorsement note…" rows={4}
          style={{ width: '100%', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 8, color: '#A0D8A8', fontSize: 13, lineHeight: 1.6, resize: 'vertical', padding: '8px 10px', outline: 'none' }} />
      </Card>

      {/* Allergies */}
      <Card s={{ background: '#1A0A0A', border: '1px solid #3A1515' }}>
        <SLabel color="#FF4545">Allergies</SLabel>
        <div style={{ fontSize: 13, color: '#F4A0A0' }}>{cur.allergies || <span style={{ color: C.dim2, fontStyle: 'italic' }}>None documented</span>}</div>
      </Card>
      </>}{/* end issuesView === 'issues' */}

      {/* ── HISTORY TAB ── */}
      {issuesView === 'history' && (
        <>
          <Card s={{ border: parseFlash ? '1px solid #30D158' : '1px solid #2A3A4A', transition: 'border-color 0.4s' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <SLabel color="#7DC4FF" style={{ marginBottom: 0 }}>Clinical History</SLabel>
              <button onClick={() => setShowParse(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 8, background: '#0D1E30', border: '1px solid #4A9EFF66', color: '#7DC4FF', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                📋 Paste History
              </button>
            </div>
            {parseFlash && <div style={{ fontSize: 11, color: '#30D158', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>✓ Notes parsed and applied</div>}
            <Section label="CC — Chief Complaint"              field="cc"       cur={cur} setFld={setFld} rows={2} />
            <Section label="HPI — History of Present Illness" field="hpi"      cur={cur} setFld={setFld} rows={4} />
            <Section label="PMH — Past Medical History"        field="pmh"      cur={cur} setFld={setFld} rows={3} />
            <Section label="FH — Family History"               field="fh"       cur={cur} setFld={setFld} rows={2} />
            {cur.sex === 'F' && <OBGYNSection cur={cur} patch={patch} />}
            <Section label="Sexual History"                    field="sexualHx" cur={cur} setFld={setFld} />
            <Section label="PSH — Personal and Social History" field="psh"      cur={cur} setFld={setFld} rows={2} />
            <Section label="ROS — Review of Systems"           field="ros"      cur={cur} setFld={setFld} rows={3} />
            <Section label="PE — Physical Exam"                field="pe"       cur={cur} setFld={setFld} rows={3} />
          </Card>
          {showParse && <ParseModal onApply={handleApplyParsed} onClose={() => setShowParse(false)} />}
        </>
      )}

      {/* ── SURGERY TAB ── */}
      {issuesView === 'surgery' && (
        <>
          <Card s={{ border: '1px solid #2A1A3A', background: '#0C0A18' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <SLabel color="#BF5AF2" mb={0}>Surgery</SLabel>
              <span style={{ marginLeft: 6, fontSize: 10, color: '#BF5AF2', fontWeight: 700, background: '#BF5AF222', padding: '1px 6px', borderRadius: 4 }}>{procedureList.length}</span>
              <button onClick={addProc} style={{ marginLeft: 'auto', background: '#1A0A2A', color: '#BF5AF2', border: '1px solid #2A1A3A', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>+ Add</button>
            </div>
            {procedureList.length === 0 && <div style={{ color: C.dim2, fontSize: 13, padding: '6px 0', textAlign: 'center' }}>No procedures logged</div>}
            {procedureList.map((pr, idx) => {
              const sc = STATUS_COL[pr.status] || C.dim;
              return (
                <div key={pr.id} style={{ borderBottom: idx < procedureList.length - 1 ? `1px solid ${C.bg0}` : 'none', paddingBottom: 10, marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 8, color: '#BF5AF288', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Procedure</div>
                      <ICell value={pr.name} onChange={v => editProc(pr.id, 'name', v)}
                        onBlur={() => { if (pr.name.trim() && onNotify) onNotify('🔬 New Procedure', (cur.name || 'Patient') + (cur.room ? ' · Rm ' + cur.room : '') + ' — ' + pr.name.trim()); }}
                        ph="e.g. Central line, LP, Bronchoscopy…" />
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <div style={{ fontSize: 8, color: '#BF5AF288', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Status</div>
                      <select value={pr.status} onChange={e => editProc(pr.id, 'status', e.target.value)}
                        style={{ background: sc + '22', border: `1px solid ${sc}55`, borderRadius: 6, color: sc, fontSize: 11, fontWeight: 700, padding: '4px 6px', cursor: 'pointer', outline: 'none' }}>
                        {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <button onClick={() => delProc(pr.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 14, flexShrink: 0, marginTop: 14 }}>×</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 4 }}>
                    <div>
                      <div style={{ fontSize: 8, color: '#BF5AF288', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Date</div>
                      <div style={{ overflow: 'hidden', borderRadius: 6 }}>
                        <input type="date" value={pr.date || ''} onChange={e => editProc(pr.id, 'date', e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '5px 8px', color: C.tx, fontSize: 13, outline: 'none', colorScheme: 'dark', display: 'block' }} />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 8, color: '#BF5AF288', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Physician</div>
                      <ICell value={pr.physician} onChange={v => editProc(pr.id, 'physician', v)} ph="Dr. Name / Team" />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: '#BF5AF288', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Notes / Clearances</div>
                    <ICell value={pr.note} onChange={v => editProc(pr.id, 'note', v)} ph="Consent obtained, anaesthesia clearance, findings…" />
                  </div>
                </div>
              );
            })}
          </Card>
        </>
      )}
    </>
  );
}
